import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { Address, AddressTypes, User } from '../../../modules/security/models/user.model';
import { IColumnEntity } from '../../../modules/icolumn-entity';
import { TranslocoService } from '@ngneat/transloco';
import { MatDialog, MatDialogRef, MatSnackBar, MatSnackBarConfig } from '@angular/material';
import { UserService } from '../../../modules/security/services/user.service';
import { TreatmentsService } from '../../treatments/treatments.service';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {GooglePlaceDirective} from 'ngx-google-places-autocomplete';

@Component({
  selector: 'nuvolaweb-doctor-address-grid',
  templateUrl: './doctor-address-grid.component.html',
  styleUrls: ['./doctor-address-grid.component.scss'],
})
export class DoctorAddressGridComponent implements OnInit, AfterViewInit {

  @Input() addresses: any[] = [];
  @Input() disabledAddButton = false;
  @Output() saveAddress = new EventEmitter<any>();
  @ViewChild('hdrTpl', { static: true }) hdrTpl: TemplateRef<any>;
  @ViewChild('addressTypeTpl', { static: true }) addressTypeTpl: TemplateRef<any>;
  @ViewChild('activityNameTpl', { static: true }) activityNameTpl: TemplateRef<any>;
  @ViewChild('addressTpl', { static: true }) addressTpl: TemplateRef<any>;
  @ViewChild('zipCodeTpl', { static: true }) zipCodeTpl: TemplateRef<any>;
  @ViewChild('autocompleteTpl', { static: true }) autocompleteTpl: TemplateRef<any>;
  @ViewChild('phoneTpl', { static: true }) phoneTpl: TemplateRef<any>;
  @ViewChild('vatTPL', { static: true }) vatTPL: TemplateRef<any>;
  @ViewChild('actions', { static: true }) actions: TemplateRef<any>;
  @ViewChild('table', { static: true }) table: DatatableComponent;
  @ViewChild('tableWrapper', { static: true }) tableWrapper;
  @ViewChild('deleteRowConfirmation', { static: true }) deleteRowConfirmation: MatDialogRef<any>;
  @ViewChild('editRowConfirmation', { static: true }) editRowConfirmation: MatDialogRef<any>;
  @ViewChild('editRowConfirmationRef', { static: true }) editRowConfirmationRef: TemplateRef<any>;
  @ViewChild('createUpdateRow', { static: true }) createUpdateRow: MatDialogRef<any>;
  @ViewChild('cityTpl', { static: true }) cityTpl: MatDialogRef<any>;
  @ViewChild('placesRef', { static: true }) placesRef: GooglePlaceDirective;

  isLoading = true;
  isRequired = false;
  isEdit = false;

  public addressCreateUpdateForm: FormGroup;

  // Grid
  public ngxColumns: IColumnEntity[];
  tempAddresses: any[] = [];
  public options = {
    types: ['address']
  };
  selectedAddress: any;
  isAbleToBeDeleted: boolean;
  selectedRowdAddress = false;
  unlockedGoogleMaps = false;
  loadingDeleteStatus = -1;
  loadingEditStatus = -1;
  currentUser: User;

  currentAddress: any;
  currentRef: any;

  constructor(
    private translocoService: TranslocoService,
    private modalServide: MatDialog,
    private fb: FormBuilder,
    private userService: UserService,
    private treatmentsService: TreatmentsService,
    private cd: ChangeDetectorRef,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit() {
    this.currentUser = this.userService.readUserFromLocalStorage();

    this.addressCreateUpdateForm = this.fb.group({
      idAddress: ['', [Validators.required]],
      addressType: ['', [Validators.required]],
      addressTypeValue: [''],
      activityName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      addressTemp: [''],
      zipCode: [{ value: '', disabled: !this.allowEditAddressInfo() }],
      city: [{ value: '', disabled: !this.allowEditAddressInfo() }],
      vat: [''],
      countryCode: [{ value: '', disabled: !this.allowEditAddressInfo() }],
      location: [{ value: '', disabled: !this.allowEditAddressInfo() }],
      phone: [''],
      carrierName: [''],
      notes: [''],
      blockPhases1: [false],
      blockNextPhases: [false],
    });

    this.addressCreateUpdateForm.controls.addressType.valueChanges.subscribe(value => {
      if (value === 'I') {
        this.addressCreateUpdateForm.controls.vat.setValidators(Validators.required);
        this.addressCreateUpdateForm.controls.vat.setValue(this.addressCreateUpdateForm.controls.vat.value);
      } else {
        this.addressCreateUpdateForm.controls.vat.clearValidators();
        this.addressCreateUpdateForm.controls.vat.setValue(this.addressCreateUpdateForm.controls.vat.value);
      }
    });
    this.isLoading = true;

    this.defineColumns();

    this.recalculate();

    this.translocoService.langChanges$.subscribe(() => {
      this.translocoService.selectTranslate('REGISTRATION.COLUMNS.AddressTypeColumn').subscribe((value) => {
        this.defineColumns();
      });
    });
  }

  allowEditAddressInfo() {
    return this.currentUser.userGroup === 'ED' || this.currentUser.role === 'EX' && this.currentUser.executiveArea === 'A';
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      window.dispatchEvent(new Event('resize'));
    }, 10);
    setTimeout(() => {
      clearInterval(interval);
    }, 2500);
  }

  public handleAddressChange(address) {
    const getAddress = (address_components) => {
      let street = '';
      let streetNumber = '';
      address_components.forEach(comp => {
        if (comp.types.includes('street_number')) {
          streetNumber = comp.long_name;
        }
        if (comp.types.includes('route')) {
          street = comp.long_name;
        }
      });
      return `${street}${streetNumber ? ',' : ''} ${streetNumber}`;
    };
    const getLongName = (address_components, fieldName, fieldType = 'long_name') => {
      let value = '';
      address_components.forEach(comp => {
        if (comp.types.includes(fieldName)) {
          value = comp[fieldType];
        }
      });
      return value;
    };
    this.addressCreateUpdateForm.controls.address.setValue(getAddress(address.address_components));
    this.addressCreateUpdateForm.controls.addressTemp.setValue(getAddress(address.address_components));
    this.addressCreateUpdateForm.controls.city.setValue(getLongName(address.address_components, 'locality'));
    this.addressCreateUpdateForm.controls.zipCode.setValue(getLongName(address.address_components, 'postal_code'));
    this.addressCreateUpdateForm.controls.countryCode.setValue(getLongName(address.address_components, 'country', 'short_name'));
    this.addressCreateUpdateForm.controls.location.setValue(
      getLongName(address.address_components, 'administrative_area_level_2', 'short_name')
    );
    if (this.addressCreateUpdateForm.controls.carrierName.value === '') {
        this.addressCreateUpdateForm.controls.carrierName.setValue('FedEx');
    }
  }

  showCarrier() {
    return this.currentUser.role === 'US';
  }

  allowEditCarrier() {
    return this.currentUser.role === 'US' &&
      (this.currentUser.userGroup === 'ED' || this.currentUser.userGroup === 'SH');
  }

  allowEditCarrierForShipping() {
    if (this.addressCreateUpdateForm.controls.addressType.value !== 'S') {
      return false;
    }
    return (
      this.addressCreateUpdateForm.controls.addressType.value === 'S' &&
      this.currentUser.role === 'US' &&
      this.currentUser.userGroup === 'SH'
      );
  }

  showWarning(row) {
    return (this.currentUser.role === 'US' || this.currentUser.role === 'EX' || this.currentUser.role === 'SA') &&
      (row.blockNextPhases || row.blockPhases1);
  }

  defineColumns() {
    this.ngxColumns = [
      // tslint:disable-next-line:max-line-length
      { prop: 'addressType', name: this.translocoService.translate('REGISTRATION.COLUMNS.AddressTypeColumn'), width: 100, headerTemplate: this.hdrTpl, cellTemplate: this.addressTypeTpl },
      // tslint:disable-next-line:max-line-length
      { prop: 'activityName', name: this.translocoService.translate('REGISTRATION.COLUMNS.ActivityNameColumn'), width: 140, headerTemplate: this.hdrTpl, cellTemplate: this.activityNameTpl },
      // tslint:disable-next-line:max-line-length
      { prop: 'address', name: this.translocoService.translate('REGISTRATION.COLUMNS.AddressColumns'), width: 120, headerTemplate: this.hdrTpl, cellTemplate: this.addressTpl },
      // tslint:disable-next-line:max-line-length
      { prop: 'zipCode', name: this.translocoService.translate('REGISTRATION.COLUMNS.ZipCodeColumn'), width: 110, headerTemplate: this.hdrTpl, cellTemplate: this.zipCodeTpl },
      // tslint:disable-next-line:max-line-length
      { prop: 'country', name: this.translocoService.translate('REGISTRATION.COLUMNS.CountryColumn'), width: 110, headerTemplate: this.hdrTpl, cellTemplate: this.autocompleteTpl },
      // tslint:disable-next-line:max-line-length
      { prop: 'city', name: this.translocoService.translate('REGISTRATION.COLUMNS.CityColumn'), width: 110, headerTemplate: this.hdrTpl, cellTemplate: this.cityTpl },
      // tslint:disable-next-line:max-line-length
      { prop: 'phone', name: this.translocoService.translate('REGISTRATION.COLUMNS.PhoneColumn'), width: 100, headerTemplate: this.hdrTpl, cellTemplate: this.phoneTpl },
      // tslint:disable-next-line:max-line-length
      { prop: 'vat', name: this.translocoService.translate('REGISTRATION.VatNumberPlaceHolder'), width: 100, headerTemplate: this.hdrTpl, cellTemplate: this.vatTPL },
      { prop: 'actions', name: '', width: 160, cellTemplate: this.actions, frozenRight: true },
    ];

  }

  checkIfAddress() {
    return this.userService.readUserFromLocalStorage().role !== 'AG' ||
      this.userService.readUserFromLocalStorage().role === 'DI';
  }

  allowUnlockGoogleMaps() {
    return this.currentUser &&
      this.currentUser.role === 'US' &&
      (this.currentUser.userGroup === 'ED' || this.currentUser.userGroup === 'SH');
  }

  unlockGoogleMaps() {
    this.unlockedGoogleMaps = true;
  }

  closeCreateUpdateModal() {
    this.addressCreateUpdateForm.reset();
    this.addressCreateUpdateForm.controls.carrierName.setValue('');
    this.createUpdateRow.close();
    this.unlockedGoogleMaps = false;
  }

  allowEditAnyway() {
    return this.currentUser &&
      this.currentUser.role === 'US' &&
      (this.currentUser.userGroup === 'ED' || this.currentUser.userGroup === 'SH');
  }

  updateAnyway() {
    this.editRowConfirmation.close();
    this.editAddressModal(this.currentAddress, this.currentRef, true);
  }

  async editAddressModal(address, ref, forceEdit = false) {
    this.loadingEditStatus = address.idAddress;
    const usedInTreatment = await this.treatmentsService.existByIdAddress(address.idAddress).toPromise() as boolean;
    this.loadingEditStatus = -1;
    if (usedInTreatment && !forceEdit) {
      this.currentAddress = address;
      this.currentRef = ref;
      this.editRowConfirmation = this.modalServide.open(this.editRowConfirmationRef, { closeOnNavigation: true, disableClose: true });
      return;
    }
    this.isEdit = true;
    this.addressCreateUpdateForm.patchValue(address);
    this.addressCreateUpdateForm.controls.addressTemp.setValue(address.address);
    this.createUpdateRow = this.modalServide.open(ref, { closeOnNavigation: true,  disableClose: true });
    this.createUpdateRow.backdropClick().subscribe(() => {
      this.unlockedGoogleMaps = false;
    });
  }

  createUpdateAddress() {
    const isValid = this.checkRowValidation(this.addressCreateUpdateForm.getRawValue());
    if (!isValid) {
      if (this.addressCreateUpdateForm.controls.address.errors) {
        this.addressCreateUpdateForm.controls.addressTemp.reset();
        this.addressCreateUpdateForm.controls.addressTemp.setErrors({'incorrect': true});
      }
      this.addressCreateUpdateForm.markAllAsTouched();
      this.snackBar.open(this.translocoService.translate('REGISTRATION.errorMessageMandatory'), 'X', {
        duration: 3000,
        panelClass: 'warning',
        horizontalPosition: 'right',
        verticalPosition: 'top',
      } as MatSnackBarConfig);
      return;
    }
    const type = AddressTypes.find(x => x.key === this.addressCreateUpdateForm.controls.addressType.value);
    this.addressCreateUpdateForm.controls.addressTypeValue.setValue(type.value);
    if (this.isEdit) {
      this.addresses = this.addresses.map(address => {
        if (address.idAddress === this.addressCreateUpdateForm.controls.idAddress.value) {
          const newAddress = this.addressCreateUpdateForm.getRawValue();
          newAddress.blockPhases1 = this.addressCreateUpdateForm.getRawValue().blockPhases1 || false;
          newAddress.blockNextPhases = this.addressCreateUpdateForm.getRawValue().blockNextPhases || false;
          return {
            ...address,
            ...newAddress
          };
        }
        return address;
      });
    } else {
      const newAddress = this.addressCreateUpdateForm.getRawValue();
      newAddress.blockPhases1 = this.addressCreateUpdateForm.getRawValue().blockPhases1 || false;
      newAddress.blockNextPhases = this.addressCreateUpdateForm.getRawValue().blockNextPhases || false;
      this.addresses = [newAddress, ...this.addresses];
    }
    this.saveAddress.emit(this.addresses);
    this.closeCreateUpdateModal();
  }

  addAddress(ref) {
    this.isEdit = false;
    this.addressCreateUpdateForm.controls.idAddress.setValue(0);
    this.createUpdateRow = this.modalServide.open(ref, { closeOnNavigation: true, disableClose: true });
    this.createUpdateRow.backdropClick().subscribe(() => {
      this.unlockedGoogleMaps = false;
    });
  }

  addAddressProp(ref) {
    this.closeInformModal();
    this.addAddress(ref);
  }

  deleteRow(removeNotSave: boolean) {
    if (removeNotSave) {
      this.addresses = [...this.addresses.filter(x => x.idAddress !== 0)];
      this.saveAddress.emit(this.addresses);
      return;
    }
    if (this.selectedAddress) {
      this.addresses = [...this.addresses.filter(x => x.iterator !== this.selectedAddress.iterator)];
      this.saveAddress.emit(this.addresses);
    }
    this.closeDeleteModal();
  }

  closeDeleteModal() {
    this.deleteRowConfirmation.close();
  }

  closeInformModal() {
    this.editRowConfirmation.close();
  }

  private recalculate() {
    this.addresses.forEach((x, i) => {
      x = this.rowPrepare(x);
      x.iterator = i;
    });

    this.addresses = [...this.addresses];
    this.tempAddresses = [...this.addresses];
    this.isLoading = false;
  }

  rowPrepare(address: any) {
    address.enabled = false;
    address.idAddress = address.idAddress ? address.idAddress : 0;
    const type = AddressTypes.find(x => x.key === address.addressType);
    if (type) {
      address.addressTypeValue = type.value;
    }
    return address;
  }

  checkRowValidation(selectedRow: any, f?: any) {
    this.isRequired = false;
    let result = true;
    if (!selectedRow.addressType) {
      result = false;
    }

    if (!selectedRow.address || selectedRow.address.length > 200) {
      this.selectedRowdAddress = true;
      result = false;
    }

    if (selectedRow.zipCode && selectedRow.zipCode.length > 12) {
      result = false;
    }

    if (selectedRow.city && selectedRow.city.length > 30) {
      result = false;
    }

    if (selectedRow.countryCode && selectedRow.countryCode.length > 60) {
      result = false;
    }

    if (selectedRow.notes && selectedRow.notes.length > 50) {
      result = false;
    }

    if (selectedRow.location && selectedRow.location.length > 30) {
      result = false;
    }

    if (selectedRow.carrierName && selectedRow.carrierName.length > 30) {
      result = false;
    }

    // if (!selectedRow.countryName) {
    //   result = false;
    // }

    if (!selectedRow.countryCode) {
      result = false;
    }

    if (selectedRow.addressType === 'I' && !selectedRow.vat) {
      result = false;
    }

    return result;
  }



  async openDeleteRowModal(templateRef, row: Address) {
    if (row.idAddress === 0) {
      this.deleteRow(true);
      return;
    }
    this.loadingDeleteStatus = row.idAddress;
    const usedInTreatment = await this.treatmentsService.existByIdAddress(row.idAddress).toPromise() as boolean;
    const shippingAddresses = this.addresses.filter(x => x.addressType === row.addressType);
    if (shippingAddresses.length === 1) {
      let string = '';
      switch (row.addressType) {
        case 'S':
          string = 'users.notAbleToDeleteLastShipping';
          break;
        case 'R':
          string = 'users.notAbleToDeleteLastReturn';
          break;
        case 'I':
          string = 'users.notAbleToDeleteLastInvoice';
          break;
        default:
          string = 'users.notAbleToDeleteLastShipping';
          break;
      }
      this.snackBar.open(this.translocoService.translate(string), 'X', {
        duration: 3000,
        panelClass: 'warning',
        horizontalPosition: 'right',
        verticalPosition: 'top',
      } as MatSnackBarConfig);
      this.loadingDeleteStatus = -1;
      this.cd.detectChanges();
      return;
    }
    this.loadingDeleteStatus = -1;
    this.isAbleToBeDeleted = !usedInTreatment;
    this.selectedAddress = row;
    this.deleteRowConfirmation = this.modalServide.open(templateRef, { closeOnNavigation: true, disableClose: true });
  }

}
